import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { Action } from "../ngrx.interface";
import { MapActionTypes, CustomViewActionTypes } from "../actions/types";
import { SnackBarLevel } from "src/app/shared/types/enums/map.enums";
import { SnackbarService } from "src/app/shared/services/snackbar.service";
import { CustomViewService } from "src/app/shared/services/custom-view.service";
import {
  ICreateCustomViewResponse,
  ICustomView,
  ICustomViewDeletedPayload,
  ICustomViewPayload,
  IDeleteCustomViewResponse,
  IReadCustomViewResponse,
  IUpdateCustomViewResponse,
} from "src/app/shared/types/interfaces/custom-view.interface";
import { CustomViewActions } from "../actions";

@Injectable()
/**
 * geeral ngrx side effects
 */
export class CustomViewEffects {
  private actions$ = inject(Actions);
  private customViewService = inject(CustomViewService);
  private snackBarService = inject(SnackbarService);

  Observable = Observable;

  readCustomView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomViewActionTypes.ReadCustomView),
      switchMap((action: Action): Observable<Action> => {
        return this.customViewService.readCustomView(action.payload.id).pipe(
          map((res: IReadCustomViewResponse) => {
            const customViewPayload: ICustomViewPayload = res.output.data[0];
            let customView: ICustomView;
            if (!customViewPayload) {
              // existing customView not found, so return 'new' here
              customView = this.customViewService.buildNewCustomView();
            } else {
              customView = this.customViewService.buildCustomViewFromPayload(customViewPayload);
            }
            return CustomViewActions.setCustomView({
              payload: {
                ...customView!,
                holdMap: action.payload.holdMap,
                refreshPage: action.payload.refreshPage,
              },
            });
          }),
          catchError((error: any): Observable<Action> => {
            this.snackBarService.open(`Error reading user preferences: ${error.message}`, "Error", SnackBarLevel.Error);
            // note: return an array with 2 actions here:
            const newCustomView = this.customViewService.buildNewCustomView();
            return of(
              {
                type: MapActionTypes.ServiceError,
                payload: error,
              },
              CustomViewActions.setCustomView({
                payload: { ...newCustomView, holdMap: action.payload.holdMap },
              })
            );
          })
        );
      })
    )
  );

  createCustomView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomViewActionTypes.CreateCustomView),
      switchMap((action: Action): Observable<Action> => {
        // note: 2024-08-12 - userPreferences api requires numeric values to be strings,
        // but we store as numbers in our app state
        let customView: ICustomView = action.payload;
        let customViewPayload: ICustomViewPayload;

        customViewPayload = this.customViewService.buildPayloadFromCustomView(customView);

        return this.customViewService.createCustomView(customViewPayload).pipe(
          map((res: ICreateCustomViewResponse) => {
            if (res.output.data.success) {
              return CustomViewActions.setCustomView({
                payload: action.payload,
              });
            } else {
              // create failed so create an invalid custom view
              let invalidCustomView = this.customViewService.buildInvalidCustomView();
              return CustomViewActions.setCustomView({
                payload: invalidCustomView,
              });
            }
          }),
          catchError((error: any): Observable<Action> => {
            this.snackBarService.open(
              `Error creating user preferences: ${error.message}`,
              "Error",
              SnackBarLevel.Error
            );
            // note: return an array with 2 actions here:
            let invalidCustomView = this.customViewService.buildInvalidCustomView();
            return of(
              {
                type: MapActionTypes.ServiceError,
                payload: error,
              },
              CustomViewActions.setCustomView({ payload: invalidCustomView })
            );
          })
        );
      })
    )
  );

  updateCustomView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomViewActionTypes.UpdateCustomView),
      switchMap((action: Action): Observable<Action> => {
        let customView: ICustomView = action.payload;
        let eventType = customView.custom_view_event_type;
        let customViewPayload: ICustomViewPayload;
        customViewPayload = this.customViewService.buildPayloadFromCustomView(customView);
        return this.customViewService.updateCustomView(customViewPayload).pipe(
          map((res: IUpdateCustomViewResponse) => {
            const customViewPayload: ICustomViewPayload = res.output.data;
            const customView: ICustomView = this.customViewService.buildCustomViewFromPayload(customViewPayload);
            let customViewMessage = "";
            let snackBarLevel: SnackBarLevel = SnackBarLevel.Success;
            if (eventType === "create") {
              customViewMessage = `${customViewPayload.custom_views?.[customViewPayload.custom_views.length - 1].customViewName} view saved.`;
              snackBarLevel = SnackBarLevel.Success;
            } else if (eventType === "manage" || eventType === "info") {
              customViewMessage = "Changes saved.";
              snackBarLevel = SnackBarLevel.Success;
            } else if (eventType === "limit") {
              customViewMessage = `${5 - (customViewPayload.custom_views?.length ?? 0)} views successfully deleted.`;
              snackBarLevel = SnackBarLevel.Success;
            } else {
              customViewMessage = "View successfully deleted.";
              snackBarLevel = SnackBarLevel.Success;
            }
            this.snackBarService.open(customViewMessage, "", snackBarLevel);
            return CustomViewActions.setCustomView({
              payload: { ...customView, holdMap: true },
            });
          }),
          catchError((error: any): Observable<Action> => {
            this.snackBarService.open("The custom view could not be saved. Please try again.", "", SnackBarLevel.Error);
            return of({
              type: MapActionTypes.ServiceError,
              payload: error,
            });
          })
        );
      })
    )
  );

  deleteCustomView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomViewActionTypes.DeleteCustomView),
      switchMap((action: Action): Observable<Action> => {
        const userId = action.payload.id;
        return this.customViewService.deleteCustomView(userId).pipe(
          map((res: IDeleteCustomViewResponse) => {
            const deletePayload: ICustomViewDeletedPayload = {
              id: userId,
              is_active: res.output.data.is_active,
            };
            return CustomViewActions.setCustomViewDeleted({
              payload: deletePayload,
            });
          }),
          catchError((error: any): Observable<Action> => {
            this.snackBarService.open(
              `Error deleting user preferences: ${error.message}`,
              "Error",
              SnackBarLevel.Error
            );
            return of({
              type: MapActionTypes.ServiceError,
              payload: error,
            });
          })
        );
      })
    )
  );

  switchCustomView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomViewActionTypes.SwitchCustomView),
      switchMap((action: Action): Observable<Action> => {
        let customView: ICustomView = action.payload;
        let customViewPayload: ICustomViewPayload;
        customViewPayload = this.customViewService.buildPayloadFromCustomView(customView);
        return this.customViewService.updateCustomView(customViewPayload).pipe(
          map((res: IUpdateCustomViewResponse) => {
            const customViewPayload: ICustomViewPayload = res.output.data;
            const customView: ICustomView = this.customViewService.buildCustomViewFromPayload(customViewPayload);
            this.snackBarService.open(
              `${customViewPayload.custom_views?.[0].customViewName} view applied.`,
              "",
              SnackBarLevel.InfoDark
            );
            return CustomViewActions.setCustomView({
              payload: { ...customView, holdMap: true },
            });
          }),
          catchError((error: any): Observable<Action> => {
            this.snackBarService.open(
              `Error updating user preferences: ${error.message}`,
              "Error",
              SnackBarLevel.Error
            );
            return of({
              type: MapActionTypes.ServiceError,
              payload: error,
            });
          })
        );
      })
    )
  );
}
